import React from "react";
import { Drawer } from "./Drawer";
import type { DrawerProps, StepsProps } from "../utils/local-antd";
import { AtiraImage } from "./Image";
import { Images } from "../assets";
import { Breakpoints } from "../theme/Breakpoints";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { StepperStep } from "./stepper/StepperStep";
import styled from "styled-components";
import { Flex } from "./Flex";
import { Spacing } from "../theme/Spacing";
import { AtiraStepperWithContext } from "./stepper/AtiraStepperWithContext";
import { FontSize } from "./Text";
import { ThemeKeys, useTheme } from "../ThemeContext";
import capitalize from "lodash/capitalize";

const StyledDrawer = styled(Drawer)`
  .ant-drawer-title {
    font-size: ${FontSize.l} !important;
    color: ${({ theme }) => theme.main};
  }
`;

const StepperWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;

  .stepper-buttons-wrapper {
    position: fixed;
    inset-inline-end: ${Spacing.xl};
    bottom: ${Spacing.xl};
  }
`;

type DrawerWithStepperProps = DrawerProps & {
  editMode?: boolean;
  submitLoading: boolean;
  onSubmit: () => Promise<void>;
  stepperItems: StepsProps["items"];
  steps: React.ReactNode[];
  stepsExtraComponent?: React.ReactNode;
  nextButtonDataTestId?: string;
  prevButtonDataTestId?: string;
};

export const DrawerWithStepper: React.FC<DrawerWithStepperProps> = ({
  editMode,
  submitLoading,
  onSubmit,
  stepperItems = [],
  steps,
  stepsExtraComponent,
  nextButtonDataTestId,
  prevButtonDataTestId,
  ...props
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { selectedTheme } = useTheme();
  // fallback for older saved themes/saved themes
  const currentTheme =
    (selectedTheme as any) === "light" ? ThemeKeys.PURPLE : selectedTheme;

  return (
    <StyledDrawer
      width={"100%"}
      destroyOnHidden
      extra={
        <AtiraImage
          src={
            Images[
              `AtiraSpaceLogoIcon${capitalize(currentTheme)}` as keyof typeof Images
            ]
          }
          width={"2rem"}
        />
      }
      push={false}
      {...props}
    >
      <StepperWrapper>
        <AtiraStepperWithContext
          submitButtonTitle={t(
            `common.${editMode === true ? "edit" : "create"}`,
          )}
          submitLoading={submitLoading}
          onSubmit={onSubmit}
          components={steps.map((step, index) => (
            <StepperStep key={index} children={step} />
          ))}
          items={stepperItems}
          orientation={
            width < Number(Breakpoints.DESKTOP.replace("px", ""))
              ? "horizontal"
              : "vertical"
          }
          prevButtonLabel={t("common.back")}
          nextButtonLabel={t("common.next")}
          editMode={editMode === true}
          stepsExtraComponent={stepsExtraComponent}
          nextButtonDataTestId={nextButtonDataTestId}
          prevButtonDataTestId={prevButtonDataTestId}
        />
      </StepperWrapper>
    </StyledDrawer>
  );
};
